import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FilterContext } from "../../../context/Filter";
import { axInst } from "../../../components/Service/Service";

// Helper function to get ARN payload and merge with filter payload
export const getArnPayload = (parArn, childArn, filterPayload = {}) => {
  const base = {
    ...(childArn ? { childArn } : { parArn }),
    ...filterPayload,
  };

  Object.keys(base).forEach((key) => {
    if (Array.isArray(base[key]) && base[key].length === 0) {
      delete base[key];
    }
  });

  return base;
};

// API Call 1 - Fetch dropdown options with abort signal and payload
const fetchJCDetailsOptions = async (
  parArn,
  childArn,
  jcDropDownPayload,
  searchType,
  abortSignal
) => {
  if (!parArn && !childArn) return [];
  if (!searchType) return [];

  const values = getArnPayload(parArn, childArn, jcDropDownPayload);
  const payload = {
    ...values,
    search_type: searchType,
  };

  try {
    const res = await axInst.post(
      `${process.env.REACT_APP_API_URL}dropdownOptions`,
      payload,
      { signal: abortSignal }
    );
    let result = [];

    // If the API directly returns an array
    if (Array.isArray(res.data)) {
      result = res.data;
    }
    // If it returns { result: [ ... ] }
    else if (Array.isArray(res.data.result)) {
      result = res.data.result;
    }
    // Fallback (for nested structure)
    else if (
      res.data.result?.aggregations?.groupby?.buckets?.all_docs?.StateWiseCount
        ?.buckets
    ) {
      result =
        res.data.result.aggregations.groupby.buckets.all_docs.StateWiseCount
          .buckets;
    }

    return result || [];
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching dropdown options:", error);
    return [];
  }
};

// API Call 2 - Fetch table data with abort signal and payload
const fetchJCDetailsTableData = async (
  parArn,
  childArn,
  jcPayload,
  searchType,
  dropdownValue,
  abortSignal
) => {
  if (!parArn && !childArn) return [];
  if (!searchType || !dropdownValue) return [];

  const values = getArnPayload(parArn, childArn, jcPayload);
  const payload = {
    ...values,
    search_type: searchType,
    dropdownValue: dropdownValue,
  };

  try {
    const res = await axInst.post(
      `${process.env.REACT_APP_API_URL}detailedJobCardTableData`,
      payload,
      { signal: abortSignal }
    );
    const hits = res?.data?.result;
    return Array.isArray(hits) ? hits : [];
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching table data:", error);
    return [];
  }
};

// API Call 3 - Fetch balance active coupon amount with abort signal
const fetchbalanceActiveCuponAmount = async (
  searchType,
  dropdownValue,
  abortSignal
) => {
  if (!searchType || !dropdownValue) return {};

  const payload = {
    search_type: searchType,
    dropdownValue: dropdownValue,
  };

  try {
    const res = await axInst.post(
      `${process.env.REACT_APP_API_URL}balanceActiveCuponAmount`,
      payload,
      { signal: abortSignal }
    );
    return res.data;
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching balanceActiveCuponAmount:", error);
    return {};
  }
};

// API Call 4 - Fetch expired coupon amount with abort signal
const fetchexpiredCuponAmount = async (
  searchType,
  dropdownValue,
  status,
  abortSignal
) => {
  if (!searchType || !dropdownValue || !status) return {};

  const payload = {
    search_type: searchType,
    dropdownValue: dropdownValue,
    status,
  };

  try {
    const res = await axInst.post(
      `${process.env.REACT_APP_API_URL}expiredCuponAmount`,
      payload,
      { signal: abortSignal }
    );
    return res.data;
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching expiredCuponAmount:", error);
    return {};
  }
};

// API Call 5 - Fetch total coupon amount with abort signal
const fetchtotalCuponAmount = async (
  searchType,
  dropdownValue,
  abortSignal
) => {
  if (!searchType || !dropdownValue) return {};

  const payload = {
    search_type: searchType,
    dropdownValue: dropdownValue,
  };

  try {
    const res = await axInst.post(
      `${process.env.REACT_APP_API_URL}totalCuponAmount`,
      payload,
      { signal: abortSignal }
    );
    return res.data;
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching totalCuponAmount:", error);
    return {};
  }
};

// API Call 6 - Fetch availed coupon amount with abort signal
const fetchavailedCuponAmount = async (
  searchType,
  dropdownValue,
  abortSignal
) => {
  if (!searchType || !dropdownValue) return {};

  const payload = {
    search_type: searchType,
    dropdownValue: dropdownValue,
  };

  try {
    const res = await axInst.post(
      `${process.env.REACT_APP_API_URL}availedCuponAmount`,
      payload,
      { signal: abortSignal }
    );
    return res.data;
  } catch (error) {
    if (error.name === "AbortError") {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching availedCuponAmount:", error);
    return {};
  }
};

const Jcdetailed = () => {
  const {
    arn: parArn,
    childArn,
    jcDropDownPayload,
    jcPayload,
  } = useContext(FilterContext);

  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [loading, setLoading] = useState(false);

  const [isValueDropdownOpen, setIsValueDropdownOpen] = useState(false);
  const [valueSearch, setValueSearch] = useState("");

  const [detailsJCOptionsData, setDetailsJCOptionsData] = useState([]);
  const [detailsJCTableData, setDetailsJCTableData] = useState([]);
  const [balanceActiveCuponAmount, setBalanceActiveCuponAmount] = useState({});
  const [expiredCuponAmount, setExpiredCuponAmount] = useState({});
  const [totalCuponAmount, setTotalCuponAmount] = useState({});
  const [availedCuponAmount, setAvailedCuponAmount] = useState({});

  const currentRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  const componentMountedRef = useRef(true);
  const resetInProgressRef = useRef(false);
  const lastDropDownPayloadRef = useRef(null);
  const lastJcPayloadRef = useRef(null);

  const isContextReady = Boolean(parArn || childArn);

  // Remove search_type and dropDownValue from payloads for clean API calls
  const {
    search_type: dropdownSearchType,
    dropDownValue: dropdownValue1,
    ...sanitizedDropDownPayload
  } = jcDropDownPayload || {};
  const {
    search_type: jcSearchType,
    dropDownValue: dropdownValue2,
    ...sanitizedJcPayload
  } = jcPayload || {};

  // Cancel any ongoing requests
  const cancelCurrentRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentRequestRef.current = null;
  }, []);

  // Enhanced reset function
  const resetState = useCallback(() => {
    // console.log("Resetting JcDetailed state...");
    resetInProgressRef.current = true;

    cancelCurrentRequests();
    setSelectedFilter("");
    setSelectedValue("");
    setDetailsJCOptionsData([]);
    setDetailsJCTableData([]);
    setBalanceActiveCuponAmount({});
    setExpiredCuponAmount({});
    setTotalCuponAmount({});
    setAvailedCuponAmount({});
    setLoading(false);

    // Clear the reset flag after a short delay
    setTimeout(() => {
      resetInProgressRef.current = false;
    }, 100);
  }, [cancelCurrentRequests]);

  // Enhanced first dropdown handler
  const handleFirstDropdownChange = useCallback(
    async (e) => {
      const selectedFilterValue = e.target.value;

      // Prevent operations during reset
      if (resetInProgressRef.current) {
        // console.log("Ignoring dropdown change during reset");
        return;
      }

      // Cancel any ongoing requests
      cancelCurrentRequests();

      setSelectedFilter(selectedFilterValue);
      setSelectedValue("");
      setDetailsJCOptionsData([]);
      setDetailsJCTableData([]);
      setBalanceActiveCuponAmount({});
      setExpiredCuponAmount({});
      setTotalCuponAmount({});
      setAvailedCuponAmount({});

      if (!selectedFilterValue || !isContextReady) {
        return;
      }

      setLoading(true);

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const requestId = Date.now();
      currentRequestRef.current = requestId;

      try {
        const options = await fetchJCDetailsOptions(
          parArn,
          childArn,
          sanitizedDropDownPayload,
          selectedFilterValue,
          abortControllerRef.current.signal
        );

        // Check if component is still mounted and request is still current
        if (
          componentMountedRef.current &&
          currentRequestRef.current === requestId &&
          !resetInProgressRef.current
        ) {
          setDetailsJCOptionsData(options || []);
        }
      } catch (error) {
        if (
          error.name !== "AbortError" &&
          componentMountedRef.current &&
          currentRequestRef.current === requestId &&
          !resetInProgressRef.current
        ) {
          console.error("Error fetching JC options:", error);
          setDetailsJCOptionsData([]);
        }
      } finally {
        if (
          componentMountedRef.current &&
          currentRequestRef.current === requestId &&
          !resetInProgressRef.current
        ) {
          setLoading(false);
          currentRequestRef.current = null;
        }
      }
    },
    [
      parArn,
      childArn,
      sanitizedDropDownPayload,
      isContextReady,
      cancelCurrentRequests,
    ]
  );

  // Enhanced second dropdown handler - fetches all data
  const handleSecondDropdownChange = useCallback(
    async (e) => {
      const selectedOption = e.target.value;

      // Prevent operations during reset
      if (resetInProgressRef.current) {
        // console.log("Ignoring dropdown change during reset");
        return;
      }

      // Cancel any ongoing requests
      cancelCurrentRequests();

      setSelectedValue(selectedOption);
      setDetailsJCTableData([]);
      setBalanceActiveCuponAmount({});
      setExpiredCuponAmount({});
      setTotalCuponAmount({});
      setAvailedCuponAmount({});

      if (!selectedOption || !selectedFilter || !isContextReady) {
        return;
      }

      setLoading(true);

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const requestId = Date.now();
      currentRequestRef.current = requestId;

      try {
        // Fetch all data in parallel
        const [tableData, balanceData, expiredData, totalData, availedData] =
          await Promise.all([
            fetchJCDetailsTableData(
              parArn,
              childArn,
              sanitizedJcPayload,
              selectedFilter,
              selectedOption,
              abortControllerRef.current.signal
            ),
            fetchbalanceActiveCuponAmount(
              selectedFilter,
              selectedOption,
              abortControllerRef.current.signal
            ),
            fetchexpiredCuponAmount(
              selectedFilter,
              selectedOption,
              "Closed",
              abortControllerRef.current.signal
            ),
            fetchtotalCuponAmount(
              selectedFilter,
              selectedOption,
              abortControllerRef.current.signal
            ),
            fetchavailedCuponAmount(
              selectedFilter,
              selectedOption,
              abortControllerRef.current.signal
            ),
          ]);

        // Check if component is still mounted and request is still current
        if (
          componentMountedRef.current &&
          currentRequestRef.current === requestId &&
          !resetInProgressRef.current
        ) {
          setDetailsJCTableData(tableData || []);
          setBalanceActiveCuponAmount(balanceData || {});
          setExpiredCuponAmount(expiredData || {});
          setTotalCuponAmount(totalData || {});
          setAvailedCuponAmount(availedData || {});
        }
      } catch (error) {
        if (
          error.name !== "AbortError" &&
          componentMountedRef.current &&
          currentRequestRef.current === requestId &&
          !resetInProgressRef.current
        ) {
          console.error("Error fetching JC data:", error);
          setDetailsJCTableData([]);
          setBalanceActiveCuponAmount({});
          setExpiredCuponAmount({});
          setTotalCuponAmount({});
          setAvailedCuponAmount({});
        }
      } finally {
        if (
          componentMountedRef.current &&
          currentRequestRef.current === requestId &&
          !resetInProgressRef.current
        ) {
          setLoading(false);
          currentRequestRef.current = null;
        }
      }
    },
    [
      parArn,
      childArn,
      sanitizedJcPayload,
      selectedFilter,
      isContextReady,
      cancelCurrentRequests,
    ]
  );

  const filteredOptions = useMemo(() => {
    if (!valueSearch) return detailsJCOptionsData;

    return detailsJCOptionsData.filter((item) =>
      String(item.key)
        .toLowerCase()
        .includes(valueSearch.toLowerCase())
    );
  }, [detailsJCOptionsData, valueSearch]);

  // Enhanced effect for payload changes
  useEffect(() => {
    if (!isContextReady) {
      resetState();
      return;
    }

    const currentDropDownPayload = JSON.stringify(jcDropDownPayload);
    const currentJcPayload = JSON.stringify(jcPayload);

    // If this is the first load, just set the references
    if (
      lastDropDownPayloadRef.current === null &&
      lastJcPayloadRef.current === null
    ) {
      lastDropDownPayloadRef.current = currentDropDownPayload;
      lastJcPayloadRef.current = currentJcPayload;
      return;
    }

    // If either payload has changed, reset everything
    if (
      lastDropDownPayloadRef.current !== currentDropDownPayload ||
      lastJcPayloadRef.current !== currentJcPayload
    ) {
      // console.log("JC payload changed, resetting JcDetailed");

      // Set reset flag to prevent race conditions
      resetInProgressRef.current = true;

      // Cancel any ongoing requests immediately
      cancelCurrentRequests();

      // Reset state
      setSelectedFilter("");
      setSelectedValue("");
      setDetailsJCOptionsData([]);
      setDetailsJCTableData([]);
      setBalanceActiveCuponAmount({});
      setExpiredCuponAmount({});
      setTotalCuponAmount({});
      setAvailedCuponAmount({});
      setLoading(false);

      // Update payload references
      lastDropDownPayloadRef.current = currentDropDownPayload;
      lastJcPayloadRef.current = currentJcPayload;

      // Clear reset flag after state updates
      setTimeout(() => {
        resetInProgressRef.current = false;
      }, 150);
    }
  }, [
    jcDropDownPayload,
    jcPayload,
    isContextReady,
    resetState,
    cancelCurrentRequests,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;

    return () => {
      componentMountedRef.current = false;
      cancelCurrentRequests();
    };
  }, [cancelCurrentRequests]);

  // Enhanced conditions for UI states
  const isSecondDropdownDisabled =
    !selectedFilter ||
    !Array.isArray(detailsJCOptionsData) ||
    detailsJCOptionsData.length === 0 ||
    loading ||
    !isContextReady ||
    resetInProgressRef.current;

  const showTable =
    detailsJCTableData.length > 0 &&
    !loading &&
    isContextReady &&
    !resetInProgressRef.current;

  const showCouponStats =
    selectedValue && !loading && isContextReady && !resetInProgressRef.current;

  // Memoized table data
  const data = useMemo(() => {
    return Array.isArray(detailsJCTableData)
      ? detailsJCTableData.map((item, index) => {
        const source = item._source || item || {};
        const fields = item.fields || {};
        return {
          srNo: index + 1,
          month: source.ORDER_MONTHYEAR || "",
          createdDate: source.JC_CREATED_DT_STR || "",
          state: source.DLR_SLS_STATE || "",
          workshop: source.DLR_NAME || "",
          jcNumber: source.ORDER_NUM || "",
          serviceType: source.SR_SR_CAT_TYPE_CD || "",
          status: source.STATUS_CD || "",
          tatHours: fields.TAT_hrs?.[0] ?? "",
          billAmount: source.Bill_Amount ?? "",
          kmRun: source.Km_run ?? "",
          pl: source.SR_ASSET_PL || "",
          chassisNumber: source.SR_ASSET_NUM || "",
          regNumber: source.VEHICLE_REGISTRATION_NUMBER || "",
        };
      })
      : [];
  }, [detailsJCTableData]);

  const columns = useMemo(
    () => [
      { accessorKey: "srNo", header: "#" },
      { accessorKey: "month", header: "Month" },
      { accessorKey: "createdDate", header: "JC Created Date" },
      { accessorKey: "state", header: "State" },
      { accessorKey: "workshop", header: "Workshop Name" },
      { accessorKey: "jcNumber", header: "JC#" },
      { accessorKey: "serviceType", header: "Service Type" },
      { accessorKey: "status", header: "Status" },
      { accessorKey: "tatHours", header: "TAT(Hrs)" },
      { accessorKey: "billAmount", header: "Bill Amount" },
      { accessorKey: "kmRun", header: "Km. run" },
      { accessorKey: "pl", header: "PL" },
      { accessorKey: "chassisNumber", header: "Chassis Number" },
      { accessorKey: "regNumber", header: "Registration Number" },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {/* <div className="bg-[#2374BF] w-full mt-2 text-lg text-white text-center py-2">
        Job Card Detailed View
      </div> */}

      <div className="block md:flex gap-20 px-4 py-3 items-center justify-center">
        {/* First Dropdown */}
        <div className="block  flex-col md:flex">
          <label htmlFor="jcFilterBy" className="text-sm font-medium mb-1">
            Filter By
          </label>
          <select
            id="jcFilterBy"
            value={selectedFilter}
            onChange={handleFirstDropdownChange}
            disabled={loading || !isContextReady || resetInProgressRef.current}
            className="border border-gray-300 w-64 font-medium rounded px-3 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!isContextReady ? "Loading..." : "Select Filter"}
            </option>
            <option value="chassis_number">Chassis Number</option>
            <option value="vehicle_registration_number">
              Vehicle Registration Number
            </option>
            <option value="division_name">Division Name</option>
            <option value="service_type">Service Type Name</option>
          </select>
        </div>

        {/* Second Dropdown */}
        {/* <div className="flex flex-col">
          <label htmlFor="jcSelectValue" className="text-sm font-medium mb-1">
            Select Value
          </label>
          <select
            id="jcSelectValue"
            value={selectedValue}
            onChange={handleSecondDropdownChange}
            disabled={isSecondDropdownDisabled}
            className="border border-gray-300 w-64 font-medium rounded px-3 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!isContextReady
                ? "Loading..."
                : resetInProgressRef.current
                  ? "Resetting..."
                  : !selectedFilter
                    ? "Select Filter First"
                    : loading
                      ? "Loading options..."
                      : detailsJCOptionsData.length === 0
                        ? "No options available"
                        : "Select Value"}
            </option>
            {Array.isArray(detailsJCOptionsData) &&
              detailsJCOptionsData.map((item, index) => (
                <option key={`${item.key}-${index}`} value={item.key}>
                  {item.key}
                </option>
              ))}
          </select>
        </div> */}
        <div className="relative w-64">
          <label className="text-sm font-medium mb-1 block">
            Select Value
          </label>

          {/* Search Input */}
          <input
            type="text"
            value={valueSearch}
            placeholder={
              !selectedFilter
                ? "Select Filter First"
                : loading
                  ? "Loading options..."
                  : "Search value..."
            }
            disabled={isSecondDropdownDisabled}
            onChange={(e) => {
              setValueSearch(e.target.value);
              setIsValueDropdownOpen(true);
            }}
            onFocus={() => setIsValueDropdownOpen(true)}
            className="border border-gray-300 w-full rounded px-3 py-1 disabled:bg-gray-100"
          />

          {/* Dropdown List */}
          {isValueDropdownOpen && !isSecondDropdownDisabled && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded mt-1 max-h-48 overflow-y-auto shadow">
              {filteredOptions.length === 0 ? (
                <li className="px-3 py-2 text-gray-500 text-sm">
                  No results found
                </li>
              ) : (
                filteredOptions.map((item, index) => (
                  <li
                    key={`${item.key}-${index}`}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-100 text-sm"
                    onClick={() => {
                      setSelectedValue(item.key);
                      setValueSearch(item.key);
                      setIsValueDropdownOpen(false);

                      // 🔥 Call existing logic
                      handleSecondDropdownChange({
                        target: { value: item.key },
                      });
                    }}
                  >
                    {item.key}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

      </div>

      {/* Loading */}
      {(loading || resetInProgressRef.current) && (
        <div className="text-center text-blue-600 mt-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2">
            {resetInProgressRef.current ? "Resetting..." : "Loading..."}
          </span>
        </div>
      )}

      {/* Conditional Messages */}
      {!isContextReady && (
        <p className="text-center text-red-600 mt-4">
          Context not ready. Please ensure ARN is set.
        </p>
      )}

      {isContextReady &&
        !selectedFilter &&
        !loading &&
        !resetInProgressRef.current && (
          <p className="text-center text-gray-500 mt-4">
            Please select a filter.
          </p>
        )}

      {selectedFilter &&
        !selectedValue &&
        !loading &&
        isContextReady &&
        detailsJCOptionsData.length > 0 &&
        !resetInProgressRef.current && (
          <p className="text-center text-gray-500 mt-4">
            Please select a value.
          </p>
        )}

      {selectedFilter &&
        !loading &&
        isContextReady &&
        detailsJCOptionsData.length === 0 &&
        !resetInProgressRef.current && (
          <p className="text-center text-orange-600 mt-4">
            No options available for "{selectedFilter}" filter.
          </p>
        )}

      {/* Coupon Statistics */}
      {showCouponStats && (
        <div className="block md:flex gap-6 md:gap-3 justify-center justify-items-center mt-4">
          <div className="bg-[#2374BF] w-48 p-2 text-center rounded-lg text-white mb-2 md:mb-0">
            Total Coupon -{" "}
            {totalCuponAmount?.result?.aggregations?.groupby?.buckets
              ?.match_all_filter?.["Total_amount_coupon_generated"]?.value ?? 0}
          </div>
          <div className="bg-[#2374BF] w-48 p-2 text-center rounded-lg text-white mb-2 md:mb-0">
            Availed Coupon -{" "}
            {availedCuponAmount?.result?.aggregations?.groupby?.buckets
              ?.match_all_filter?.["Total_amout_coupon_availed"]?.value ?? 0}
          </div>
          <div className="bg-[#2374BF] w-48 p-2 text-center rounded-lg text-white mb-2 md:mb-0">
            Active Coupon -{" "}
            {balanceActiveCuponAmount?.result?.aggregations?.groupby?.buckets
              ?.match_all_filter?.["Total_amount_coupon(Balance/Active)"]
              ?.value ?? 0}
          </div>
          <div className="bg-[#2374BF] w-48 p-2 text-center rounded-lg text-white">
            Expired Coupon -{" "}
            {expiredCuponAmount?.result?.aggregations?.groupby?.buckets
              ?.match_all_filter?.["Total_amount_coupon(Balance/Active)"]
              ?.value ?? 0}
          </div>
        </div>
      )}

      {/* Table */}
      {showTable && (
        <div className="mt-4 pl-3 max-h-[300px] overflow-y-scroll overflow-x-scroll border-gray-300 rounded">
          <table className="pl-2 text-sm text-left">
            <thead className="bg-[#2374BF] sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="border px-2 py-2 font-medium text-white text-center bg-[#2374BF]"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="border px-2 py-2 whitespace-normal break-words"
                      title={cell.getContext().getValue()}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedFilter &&
        selectedValue &&
        !loading &&
        isContextReady &&
        detailsJCTableData.length === 0 &&
        !resetInProgressRef.current && (
          <p className="text-center text-gray-500 mt-4">
            No data available for the selected criteria.
          </p>
        )}
    </div>
  );
};

export default Jcdetailed;
